import { Filter, X } from "lucide-react";
import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { AuditLogTimeline } from "@/domains/audit/components";
import { useIsMobile } from "@/hooks/hooks/use-mobile";
import { BackButton, BaseNavigationBar } from "@/lib/components/navigation";
import { PageContent } from "@/lib/components/page-content";
import { useAuthStore } from "@/lib/store/auth-enhanced";
import { SettingsSearchTrigger } from "../../search/components/settings-search-trigger";

const SETTINGS_TABLE_NAMES = ["salon_configuration", "salon_operating_hours", "salon_capacity"];

export function SettingsAuditLogPage() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  if (!user?.role.some((r) => r === "ADMIN" || r === "SUPER_ADMIN")) {
    return <Navigate to="/appointments" replace />;
  }

  const hasActiveFilters = dateRange.from || dateRange.to;
  const filterCount = (dateRange.from ? 1 : 0) + (dateRange.to ? 1 : 0);

  const clearDateFilter = () => setDateRange({ from: "", to: "" });

  return (
    <>
      <BaseNavigationBar
        title="Settings Change Log"
        subtitle="Track all configuration, operating hours, and capacity changes"
        leadElement={
          isMobile ? (
            <BackButton key="back-button" onBack={() => navigate("/salon/settings")} />
          ) : undefined
        }
        trailingElements={[
          <SettingsSearchTrigger key="search-trigger" />,
          <div key="mobile-filter" className="sm:hidden">
            <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="relative">
                  <Filter className="h-4 w-4" />
                  {hasActiveFilters && (
                    <Badge
                      variant="destructive"
                      className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center text-xs"
                    >
                      {filterCount}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="h-auto">
                <SheetHeader>
                  <SheetTitle>Filter Change Log</SheetTitle>
                </SheetHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="mobile-date-from" className="text-sm mb-1.5 block">
                      From Date
                    </Label>
                    <Input
                      id="mobile-date-from"
                      type="date"
                      value={dateRange.from}
                      onChange={(e) => setDateRange((prev) => ({ ...prev, from: e.target.value }))}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Label htmlFor="mobile-date-to" className="text-sm mb-1.5 block">
                      To Date
                    </Label>
                    <Input
                      id="mobile-date-to"
                      type="date"
                      value={dateRange.to}
                      onChange={(e) => setDateRange((prev) => ({ ...prev, to: e.target.value }))}
                      className="w-full"
                    />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      onClick={clearDateFilter}
                      className="flex-1"
                      disabled={!hasActiveFilters}
                    >
                      Clear Filters
                    </Button>
                    <Button onClick={() => setIsFilterOpen(false)} className="flex-1">
                      Apply Filters
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>,
        ]}
        bottomElement={
          <div className="hidden sm:flex gap-4">
            <div>
              <Label htmlFor="date-from" className="text-sm mb-1.5 block">
                From Date
              </Label>
              <Input
                id="date-from"
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange((prev) => ({ ...prev, from: e.target.value }))}
                className="w-full bg-background"
              />
            </div>
            <div>
              <Label htmlFor="date-to" className="text-sm mb-1.5 block">
                To Date
              </Label>
              <Input
                id="date-to"
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange((prev) => ({ ...prev, to: e.target.value }))}
                className="w-full bg-background"
              />
            </div>
            {hasActiveFilters && (
              <div className="flex items-end">
                <Button variant="ghost" size="sm" onClick={clearDateFilter}>
                  Clear Filter
                </Button>
              </div>
            )}
          </div>
        }
      />

      <div className="sm:hidden">
        {hasActiveFilters && (
          <div className="px-2 py-2">
            <Card>
              <CardContent className="py-2 px-3">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground text-xs">Active filters:</span>
                  <div className="flex flex-wrap gap-1.5 flex-1">
                    {dateRange.from && (
                      <Badge variant="secondary" className="text-xs">
                        From: {new Date(dateRange.from).toLocaleDateString()}
                      </Badge>
                    )}
                    {dateRange.to && (
                      <Badge variant="secondary" className="text-xs">
                        To: {new Date(dateRange.to).toLocaleDateString()}
                      </Badge>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearDateFilter}
                    className="h-6 w-6 p-0 ml-auto"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <PageContent>
        <AuditLogTimeline tableNames={SETTINGS_TABLE_NAMES} dateRange={dateRange} />
      </PageContent>
    </>
  );
}
